import React, { useState, useEffect } from "react";
import jsPDF from "jspdf";

const COL = {
  bg:     "#C4622D",
  header: "#2D5A3D",
  text:   "#1C1C1E",
  muted:  "#8A8A8E",
  border: "#D3D1C7",
};

const getGenCats = (t) => [
  t.catFinancement   || "Financement",
  t.catAssurance     || "Assurance",
  t.catControle      || "Contrôle technique",
  t.catGarage        || "Garage",
  t.catPeage         || "Péage",
  t.catLavage        || "Lavage",
  t.catEau           || "Eau",
  t.catGaz           || "Gaz",
  t.electricite      || "Électricité",
  t.catCamping       || "Camping",
  t.catAire          || "Aire de repos",
  t.catParking       || "Parking",
  t.catHivernage     || "Hivernage",
  t.catContravention || "Contravention",
];

function fmt(n) {
  return Number(n || 0).toFixed(2).replace(".", ",") + " €";
}
function fmtDate(s) {
  return new Date(s).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "2-digit" });
}
function monthKey(s) {
  const d = new Date(s);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}
function monthLabel(key) {
  const [y, m] = key.split("-");
  return new Date(Number(y), Number(m) - 1, 1)
    .toLocaleDateString("fr-FR", { month: "long", year: "numeric" });
}
function todayISO() {
  return new Date().toISOString().split("T")[0];
}
function computeKm(pleins) {
  const kms = pleins.map(p => Number(p.km)).filter(k => k > 0);
  if (kms.length < 2) return 0;
  return Math.max(...kms) - Math.min(...kms);
}

/* ── Petits composants formulaire ─────────────────────────── */
function FL({ children, style: s }) {
  return (
    <div style={{
      fontSize: 11, fontWeight: 700, color: COL.header,
      letterSpacing: 1, marginBottom: 6, ...s,
    }}>{children}</div>
  );
}
function FI({ mb = 12, style: s, ...props }) {
  return (
    <input
      {...props}
      style={{
        width: "100%", padding: "11px 14px",
        marginBottom: mb,
        borderRadius: 10, border: `1.5px solid ${COL.border}`,
        background: "#F8F6F1", color: COL.text,
        fontSize: 14, outline: "none",
        boxSizing: "border-box", fontFamily: "inherit",
        ...s,
      }}
    />
  );
}

/* ── Courbe L/100km ───────────────────────────────────────── */
function ConsoChart({ pleins, t = {} }) {
  const sorted = [...pleins].sort((a, b) => Number(a.km) - Number(b.km));
  const data = [];
  for (let i = 1; i < sorted.length; i++) {
    const dKm = Number(sorted[i].km) - Number(sorted[i - 1].km);
    const L   = Number(sorted[i].litres);
    if (dKm > 0 && L > 0) {
      data.push({ conso: (L / dKm) * 100, label: fmtDate(sorted[i].date) });
    }
  }

  if (data.length < 2) {
    return (
      <div style={{
        background: "#EAF3DE", borderRadius: 10, padding: "16px 14px",
        textAlign: "center", color: COL.muted, fontSize: 13, marginBottom: 12,
      }}>
        {data.length === 0
          ? (t.consoMin2 || "Enregistrez au moins 2 pleins pour voir la courbe de consommation")
          : (t.consoMin1 || "Enregistrez encore 1 plein pour voir la courbe")}
      </div>
    );
  }

  const W = 320, H = 110;
  const P = { t: 8, r: 8, b: 18, l: 30 };
  const pw = W - P.l - P.r, ph = H - P.t - P.b;
  const cs = data.map(d => d.conso);
  const maxC = Math.max(...cs), minC = Math.min(...cs);
  const range = maxC - minC || 1;
  const avg = cs.reduce((a, b) => a + b, 0) / cs.length;

  const pts = data.map((d, i) => ({
    x: P.l + (i / (data.length - 1)) * pw,
    y: P.t + (1 - (d.conso - minC) / range) * ph,
    conso: d.conso,
  }));

  const line = pts.map((p, i) =>
    `${i === 0 ? "M" : "L"} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`
  ).join(" ");

  const area = `${line} L ${pts[pts.length-1].x.toFixed(1)} ${(P.t+ph).toFixed(1)} L ${pts[0].x.toFixed(1)} ${(P.t+ph).toFixed(1)} Z`;

  return (
    <div style={{
      background: "white", borderRadius: 12,
      padding: "14px 12px 6px",
      boxShadow: "0 1px 8px rgba(0,0,0,0.06)", marginBottom: 12,
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10 }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: COL.text }}>{t.consoLabel || "Consommation L/100km"}</span>
        <span style={{ fontSize: 12, color: COL.muted }}>{t.moyLabel || "Moy :"} {avg.toFixed(1)} L/100</span>
      </div>
      <svg width="100%" viewBox={`0 0 ${W} ${H}`}>
        <defs>
          <linearGradient id="cGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#2D5A3D" stopOpacity="0.3" />
            <stop offset="100%" stopColor="#2D5A3D" stopOpacity="0" />
          </linearGradient>
        </defs>
        {[0, 0.5, 1].map(frac => (
          <line key={frac}
            x1={P.l} y1={P.t + frac * ph} x2={P.l + pw} y2={P.t + frac * ph}
            stroke="rgba(255,255,255,0.3)" strokeWidth="1"
          />
        ))}
        <text x={P.l-3} y={P.t+4}          textAnchor="end" fontSize="8" fill="white">{maxC.toFixed(1)}</text>
        <text x={P.l-3} y={P.t+ph/2+3}     textAnchor="end" fontSize="8" fill="white">{avg.toFixed(1)}</text>
        <text x={P.l-3} y={P.t+ph+3}       textAnchor="end" fontSize="8" fill="white">{minC.toFixed(1)}</text>
        <path d={area} fill="url(#cGrad)" />
        <path d={line} fill="none" stroke="#2D5A3D" strokeWidth="2.5"
          strokeLinejoin="round" strokeLinecap="round" />
        {pts.map((p, i) => (
          <g key={i}>
            <circle cx={p.x} cy={p.y} r="4.5" fill="#2D5A3D" />
            <circle cx={p.x} cy={p.y} r="2"   fill="white"   />
          </g>
        ))}
        {data.length <= 6 && data.map((d, i) => (
          <text key={i} x={pts[i].x} y={P.t+ph+P.b-1}
            textAnchor="middle" fontSize="7" fill="white">
            {d.label.slice(0, 5)}
          </text>
        ))}
      </svg>
    </div>
  );
}

/* ── Onglet Carburant ─────────────────────────────────────── */
function TabCarburant({ depenses, setDepenses, t = {} }) {
  const [showForm,      setShowForm]      = useState(false);
  const [confirmId,     setConfirmId]     = useState(null);
  const [chartPeriod,   setChartPeriod]   = useState("year");
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [form, setForm] = useState({ date: todayISO(), km: "", prix: "", litres: "" });

  const pleins     = (depenses || []).filter(d => d.type === "carburant");
  const now        = new Date();
  const ckCur      = monthKey(now.toISOString());
  const pleinsMois = pleins.filter(p => monthKey(p.date) === ckCur);
  const availableMonths = [...new Set(pleins.map(p => monthKey(p.date)).filter(Boolean))].sort((a, b) => b.localeCompare(a));
  const activeMonth = selectedMonth || ckCur;
  const pleinsChart = chartPeriod === "month"
    ? pleins.filter(p => monthKey(p.date) === activeMonth)
    : pleins.filter(p => p.date && new Date(p.date).getFullYear() === now.getFullYear());
  const ago12  = new Date(now.getFullYear() - 1, now.getMonth(), 1);
  const totalCarbuMois = pleins.filter(p => monthKey(p.date) === ckCur).reduce((s, p) => s + Number(p.prix || 0), 0);
  const totalCarbu12   = pleins.filter(p => p.date && new Date(p.date) >= ago12).reduce((s, p) => s + Number(p.prix || 0), 0);

  const add = () => {
    if (!form.km || !form.prix || !form.litres) return;
    setDepenses(prev => [...(prev || []), {
      id: Date.now(), type: "carburant",
      date: form.date, km: Number(form.km),
      prix: Number(form.prix), litres: Number(form.litres),
    }]);
    setForm({ date: todayISO(), km: "", prix: "", litres: "" });
    setShowForm(false);
  };

  return (
    <div>
      {/* Toggle mois / année */}
      <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 6, marginBottom: 6 }}>
        {chartPeriod === "month" && (
          <select
            value={activeMonth}
            onChange={e => setSelectedMonth(e.target.value)}
            style={{
              padding: "4px 8px", borderRadius: 7, border: "none",
              background: "white", color: COL.header,
              fontSize: 11, fontWeight: 700, cursor: "pointer",
              fontFamily: "inherit", outline: "none",
            }}
          >
            {availableMonths.map(mk => (
              <option key={mk} value={mk} style={{ textTransform: "capitalize" }}>
                {monthLabel(mk)}
              </option>
            ))}
          </select>
        )}
        <div style={{
          display: "flex", background: "rgba(255,255,255,0.15)",
          borderRadius: 8, overflow: "hidden",
        }}>
          {[
            { id: "month", label: t.mensuel || "Mois" },
            { id: "year",  label: String(now.getFullYear()) },
          ].map(opt => (
            <button
              key={opt.id}
              onClick={() => setChartPeriod(opt.id)}
              style={{
                padding: "5px 12px", border: "none", cursor: "pointer",
                fontFamily: "inherit", fontSize: 11, fontWeight: 700,
                background: chartPeriod === opt.id ? "white" : "transparent",
                color: chartPeriod === opt.id ? COL.header : "rgba(255,255,255,0.8)",
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>
      <ConsoChart pleins={pleinsChart} t={t} />

      {!showForm && (
        <button onClick={() => setShowForm(true)} style={{
          width: "100%", padding: "13px 0", borderRadius: 10, border: "none",
          background: COL.header, color: "#F8F6F1",
          fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
          marginBottom: 12,
        }}>
          {t.nouveauPlein || "+ Nouveau plein"}
        </button>
      )}

      {showForm && (
        <div style={{
          background: "white", borderRadius: 14, padding: "16px",
          marginBottom: 12, boxShadow: "0 1px 8px rgba(0,0,0,0.07)",
        }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: COL.text, marginBottom: 14 }}>
            {t.nouveauPlein || "Nouveau plein"}
          </div>
          <FL>{t.date || "DATE"}</FL>
          <FI type="date" value={form.date} onChange={e => setForm(p => ({ ...p, date: e.target.value }))} />
          <FL>{t.kmCompteurLabel || "KM COMPTEUR"}</FL>
          <FI type="number" placeholder="45 230" value={form.km} onChange={e => setForm(p => ({ ...p, km: e.target.value }))} />
          <FL>{t.prixTotal || "PRIX TOTAL (€)"}</FL>
          <FI type="number" placeholder="82.50" value={form.prix} onChange={e => setForm(p => ({ ...p, prix: e.target.value }))} />
          <FL>{t.litres || "LITRES"}</FL>
          <FI type="number" placeholder="55.0" value={form.litres} mb={16} onChange={e => setForm(p => ({ ...p, litres: e.target.value }))} />
          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={() => setShowForm(false)} style={{
              flex: 1, padding: "11px 0", borderRadius: 10,
              border: `1.5px solid ${COL.border}`, background: "transparent",
              color: COL.muted, fontWeight: 600, cursor: "pointer", fontFamily: "inherit",
            }}>{t.annuler || "Annuler"}</button>
            <button onClick={add} style={{
              flex: 2, padding: "11px 0", borderRadius: 10, border: "none",
              background: COL.header, color: "#F8F6F1",
              fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
            }}>{t.enregistrerPlein || "Enregistrer le plein"}</button>
          </div>
        </div>
      )}

      {pleinsMois.length === 0 ? (
        <div style={{ textAlign: "center", padding: "28px", color: "white", fontSize: 13, fontWeight: 600 }}>
          {t.aucunPlein || "Aucun plein enregistré"}
        </div>
      ) : (
        <div style={{
          background: "#FFFDE7", borderRadius: 14, overflow: "hidden",
          boxShadow: "0 1px 8px rgba(0,0,0,0.06)",
          border: "1px solid #FFF176",
        }}>
          {[...pleinsMois].sort((a, b) => new Date(b.date) - new Date(a.date)).map((p, idx, arr) => (
            <div key={p.id} style={{
              display: "flex", alignItems: "center", padding: "13px 16px",
              borderBottom: idx < arr.length - 1 ? `1px solid #FFF176` : "none",
            }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: COL.text }}>
                  {Number(p.km).toLocaleString("fr-FR")} km
                </div>
                <div style={{ fontSize: 11, color: COL.muted, marginTop: 2 }}>
                  {fmtDate(p.date)} · {p.litres} L
                </div>
              </div>
              <div style={{ fontSize: 15, fontWeight: 800, color: COL.header, marginRight: 12 }}>
                {fmt(p.prix)}
              </div>
              {confirmId === p.id ? (
                <div style={{ display: "flex", gap: 6 }}>
                  <button onClick={() => setConfirmId(null)} style={{ background: "none", border: `1px solid ${COL.border}`, borderRadius: 8, padding: "4px 8px", color: COL.muted, cursor: "pointer", fontSize: 11 }}>{t.annuler || "Annuler"}</button>
                  <button onClick={() => { setDepenses(prev => prev.filter(x => x.id !== p.id)); setConfirmId(null); }} style={{ background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 8, padding: "4px 8px", color: "#DC2626", cursor: "pointer", fontSize: 11, fontWeight: 700 }}>🗑️ {t.supprimer || "Supprimer"}</button>
                </div>
              ) : (
                <button onClick={() => setConfirmId(p.id)} style={{ background: "none", border: "none", cursor: "pointer", color: COL.border, fontSize: 14 }}>✕</button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Total carburant mois + 12 mois */}
      {pleins.length > 0 && (
        <>
          <div style={{ fontSize: 10, fontWeight: 700, color: "white", letterSpacing: 1, marginBottom: 5, marginTop: 12 }}>
            {t.depensesTotalCarbu || "DÉPENSES TOTAL CARBURANT"}
          </div>
          <div style={{
            background: "#FFFDE7", borderRadius: 12, overflow: "hidden",
            boxShadow: "0 1px 6px rgba(0,0,0,0.06)",
            border: "1px solid #FFF176",
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "9px 12px", borderBottom: "1px solid #FFF176" }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: COL.text, textTransform: "capitalize" }}>{monthLabel(ckCur)}</div>
              <div style={{ fontSize: 11, fontWeight: 800, color: COL.text }}>{fmt(totalCarbuMois)}</div>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "9px 12px", borderBottom: "1.5px solid #FFF176" }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: COL.text }}>{now.getFullYear() - 1} – {now.getFullYear()}</div>
              <div style={{ fontSize: 11, fontWeight: 800, color: COL.text }}>{fmt(totalCarbu12)}</div>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 14px", borderBottom: "1.5px solid #FFF176" }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: COL.text }}>{t.douzeMois || "12 DERNIERS MOIS"}</div>
              <div style={{ fontSize: 17, fontWeight: 900, color: COL.header }}>{fmt(totalCarbu12)}</div>
            </div>
            <button
              onClick={() => {
                const pdf = new jsPDF({ unit: "mm", format: "a4" });
                const W = 210;
                const YEL = [255, 253, 231];
                const GRN = [45, 90, 61];
                const DRK = [28, 28, 30];
                const MUT = [138, 138, 142];
                let y = 20;
                pdf.setFillColor(...GRN);
                pdf.rect(0, 0, W, 28, "F");
                pdf.setFont("helvetica", "bold");
                pdf.setFontSize(16);
                pdf.setTextColor(248, 246, 241);
                pdf.text(t.depensesTotalCarbu || "DÉPENSES TOTAL CARBURANT", W / 2, 12, { align: "center" });
                pdf.setFontSize(10);
                pdf.setFont("helvetica", "normal");
                pdf.text(String(now.getFullYear()), W / 2, 22, { align: "center" });
                y = 40;
                [[monthLabel(ckCur), totalCarbuMois], [`${now.getFullYear()-1} – ${now.getFullYear()}`, totalCarbu12]].forEach(([label, val], i) => {
                  pdf.setFillColor(...YEL);
                  pdf.rect(14, y - 5, W - 28, 12, "F");
                  pdf.setFont("helvetica", "bold");
                  pdf.setFontSize(11);
                  pdf.setTextColor(...DRK);
                  pdf.text(label, 18, y + 1);
                  pdf.setTextColor(...GRN);
                  pdf.text(fmt(val), W - 16, y + 1, { align: "right" });
                  y += 18;
                });
                y += 4;
                pdf.setDrawColor(251, 192, 45);
                pdf.setLineWidth(0.5);
                pdf.line(14, y, W - 14, y);
                y += 8;
                pdf.setFont("helvetica", "bold");
                pdf.setFontSize(12);
                pdf.setTextColor(...DRK);
                pdf.text(t.douzeMois || "12 DERNIERS MOIS", 16, y);
                pdf.setTextColor(...GRN);
                pdf.text(fmt(totalCarbu12), W - 16, y, { align: "right" });
                pdf.save(`carburant-${String(now.getFullYear())}.pdf`);
              }}
              style={{
                width: "100%", padding: "10px 0",
                background: COL.header, color: "#F8F6F1",
                border: "none", fontSize: 12, fontWeight: 800,
                cursor: "pointer", fontFamily: "inherit",
              }}
            >
              📄 {t.telechargerPDF || "Télécharger en PDF"}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

/* ── Onglet Général ───────────────────────────────────────── */
function TabGeneral({ depenses, setDepenses, userId, t = {} }) {
  const GEN_CATS = getGenCats(t);
  const [form, setForm] = useState({
    date: todayISO(), categorie: GEN_CATS[0], montant: "", description: "", km: "",
  });

  const [confirmId, setConfirmId] = useState(null);

  /* ── Par mois ── */
  const list = depenses || [];
  const monthMap = {};
  list.forEach(d => {
    const k = monthKey(d.date);
    if (!monthMap[k]) monthMap[k] = { total: 0, pleins: [] };
    if (d.type !== "carburant") monthMap[k].total += Number(d.montant || 0);
    if (d.type === "carburant") monthMap[k].pleins.push(d);
  });
  const months = Object.entries(monthMap)
    .sort((a, b) => b[0].localeCompare(a[0]))
    .slice(0, 12);

  const curKey = monthKey(new Date().toISOString());
  const totalDuMois = list
    .filter(d => monthKey(d.date) === curKey && d.type !== "carburant")
    .reduce((s, d) => s + Number(d.montant || 0), 0);

  const showKm = [t.catGarage || "Garage", t.catControle || "Contrôle technique"].includes(form.categorie);

  const generals = (depenses || []).filter(d => d.type === "general" || !d.type);

  const add = () => {
    if (!form.montant) return;
    const entry = {
      id: Date.now(), type: "general",
      date: form.date, categorie: form.categorie,
      montant: Number(form.montant),
      description: form.description.trim() || form.categorie,
    };
    if (showKm && form.km) entry.km = Number(form.km);
    setDepenses(prev => [...(prev || []), entry]);
    setForm(p => ({ ...p, montant: "", description: "", km: "" }));
  };

  return (
    <div>
      {/* Formulaire */}
      <div style={{
        background: "white", borderRadius: 12, padding: "10px 12px",
        marginBottom: 8, boxShadow: "0 1px 6px rgba(0,0,0,0.06)",
      }}>
        <FL style={{ fontSize: 9, marginBottom: 3 }}>{t.date || "DATE"}</FL>
        <FI type="date" value={form.date} onChange={e => setForm(p => ({ ...p, date: e.target.value }))} mb={6} style={{ padding: "6px 9px", fontSize: 12 }} />

        <FL style={{ fontSize: 9, marginBottom: 3 }}>{t.categorie || "CATÉGORIE"}</FL>
        <select
          value={form.categorie}
          onChange={e => setForm(p => ({ ...p, categorie: e.target.value }))}
          style={{
            width: "100%", padding: "6px 9px", marginBottom: 6,
            borderRadius: 8, border: `1.5px solid ${COL.border}`,
            background: "#F8F6F1", color: COL.text,
            fontSize: 12, outline: "none", fontFamily: "inherit",
            appearance: "none", cursor: "pointer",
          }}
        >
          {GEN_CATS.map(c => <option key={c} value={c}>{c}</option>)}
        </select>

        <FL style={{ fontSize: 9, marginBottom: 3 }}>{t.montant || "MONTANT (€)"}</FL>
        <FI type="number" placeholder="0,00" value={form.montant} mb={6} style={{ padding: "6px 9px", fontSize: 12 }} onChange={e => setForm(p => ({ ...p, montant: e.target.value }))} />

        {showKm && (
          <>
            <FL style={{ fontSize: 9, marginBottom: 3 }}>{t.kilometrageCompteur || "KILOMÉTRAGE AU COMPTEUR (km)"}</FL>
            <FI type="number" placeholder="45 230" value={form.km} mb={6} style={{ padding: "6px 9px", fontSize: 12 }} onChange={e => setForm(p => ({ ...p, km: e.target.value }))} />
          </>
        )}

        <FL style={{ fontSize: 9, marginBottom: 3 }}>{t.description || "DESCRIPTION"}</FL>
        <FI placeholder={t.optionnel || "Optionnel"} value={form.description} mb={8} style={{ padding: "6px 9px", fontSize: 12 }} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} />

        <button onClick={add} style={{
          width: "100%", padding: "9px 0", borderRadius: 9, border: "none",
          background: COL.header, color: "#F8F6F1",
          fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
        }}>
          {t.enregistrerBtn || "Enregistrer"}
        </button>
      </div>

      {/* Par mois */}
      {months.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: "white", letterSpacing: 1, marginBottom: 5 }}>
            {t.total12Mois || "TOTAL DÉPENSES GÉNÉRAL 12 DERNIERS MOIS"}
          </div>
          <div style={{
            background: "#E3F2FD", borderRadius: 12, overflow: "hidden",
            boxShadow: "0 1px 6px rgba(0,0,0,0.06)",
            border: "1px solid #90CAF9",
          }}>
            {months.map(([key, data], idx) => (
              <div key={key} style={{
                display: "flex", justifyContent: "space-between", alignItems: "center",
                padding: "7px 10px",
                borderBottom: `1px solid #90CAF9`,
              }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: COL.text, textTransform: "capitalize" }}>
                  {monthLabel(key)}
                </div>
                <div style={{ fontSize: 11, fontWeight: 800, color: COL.text }}>{fmt(data.total)}</div>
              </div>
            ))}
            {/* Total */}
            <div style={{
              display: "flex", justifyContent: "space-between", alignItems: "center",
              padding: "10px 14px", borderTop: "1.5px solid #90CAF9",
            }}>
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: COL.text }}>
                  {t.douzeMois || "12 DERNIERS MOIS"}
                </div>
              </div>
              <div style={{ fontSize: 17, fontWeight: 900, color: COL.header }}>
                {fmt(months.reduce((s, [, d]) => s + d.total, 0))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Dépenses du mois + Total + PDF */}
      <div style={{ fontSize: 10, fontWeight: 700, color: "white", letterSpacing: 1, marginBottom: 5 }}>
        {t.depensesDuMois || "DÉPENSES DU MOIS"}
      </div>
      <div style={{
        background: "#E3F2FD", borderRadius: 12, overflow: "hidden",
        boxShadow: "0 1px 6px rgba(0,0,0,0.06)",
        border: "1px solid #90CAF9",
      }}>
        {generals.length === 0 ? (
          <div style={{ textAlign: "center", padding: "20px", color: COL.muted, fontSize: 12, fontWeight: 600 }}>
            {t.aucuneDepense || "Aucune dépense enregistrée"}
          </div>
        ) : (
          <>
            {[...generals].sort((a, b) => new Date(b.date) - new Date(a.date)).map((d, idx, arr) => (
              <div key={d.id} style={{
                display: "flex", alignItems: "center", padding: "7px 10px",
                borderBottom: `1px solid #90CAF9`,
              }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: COL.text }}>
                    {d.description || d.categorie}
                  </div>
                  <div style={{ fontSize: 9, color: COL.muted, marginTop: 1 }}>
                    {d.categorie} · {fmtDate(d.date)}
                    {d.km ? ` · ${Number(d.km).toLocaleString("fr-FR")} km` : ""}
                  </div>
                </div>
                <div style={{ fontSize: 11, fontWeight: 800, color: COL.text, marginRight: 8 }}>
                  {fmt(d.montant)}
                </div>
                {confirmId === d.id ? (
                  <div style={{ display: "flex", gap: 6 }}>
                    <button onClick={() => setConfirmId(null)} style={{ background: "none", border: `1px solid ${COL.border}`, borderRadius: 8, padding: "4px 8px", color: COL.muted, cursor: "pointer", fontSize: 11 }}>{t.annuler || "Annuler"}</button>
                    <button onClick={() => { setDepenses(prev => prev.filter(x => x.id !== d.id)); setConfirmId(null); }} style={{ background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 8, padding: "4px 8px", color: "#DC2626", cursor: "pointer", fontSize: 11, fontWeight: 700 }}>🗑️ {t.supprimer || "Supprimer"}</button>
                  </div>
                ) : (
                  <button onClick={() => setConfirmId(d.id)} style={{ background: "none", border: "none", cursor: "pointer", color: COL.border, fontSize: 14 }}>✕</button>
                )}
              </div>
            ))}
            {/* Total */}
            <div style={{
              display: "flex", justifyContent: "space-between", alignItems: "center",
              padding: "10px 14px", borderTop: "1.5px solid #90CAF9",
            }}>
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: COL.text }}>
                  {t.depensesTotalDuMois || "Total dépenses général du mois"}
                </div>
                <div style={{ fontSize: 10, color: COL.muted, marginTop: 1, textTransform: "capitalize" }}>
                  {monthLabel(curKey)}
                </div>
              </div>
              <div style={{ fontSize: 17, fontWeight: 900, color: COL.header }}>{fmt(totalDuMois)}</div>
            </div>
          </>
        )}
        {/* Bouton PDF */}
        {generals.length > 0 && (
          <button
            onClick={() => {
              const pdf = new jsPDF({ unit: "mm", format: "a4" });
              const W = 210;
              const GRN = [45, 90, 61];
              const BLU = [33, 150, 243];
              const DRK = [28, 28, 30];
              const MUT = [138, 138, 142];
              const BG  = [227, 242, 253];
              let y = 20;
              pdf.setFillColor(...GRN);
              pdf.rect(0, 0, W, 28, "F");
              pdf.setFont("helvetica", "bold");
              pdf.setFontSize(16);
              pdf.setTextColor(248, 246, 241);
              pdf.text(t.depensesDuMois || "DÉPENSES DU MOIS", W / 2, 12, { align: "center" });
              pdf.setFontSize(10);
              pdf.setFont("helvetica", "normal");
              pdf.text(monthLabel(curKey), W / 2, 22, { align: "center" });
              y = 38;
              pdf.setFontSize(9);
              [...generals].sort((a, b) => new Date(b.date) - new Date(a.date)).forEach((d, i) => {
                if (y > 270) { pdf.addPage(); y = 20; }
                if (i % 2 === 0) {
                  pdf.setFillColor(...BG);
                  pdf.rect(14, y - 4, W - 28, 10, "F");
                }
                pdf.setFont("helvetica", "bold");
                pdf.setTextColor(...DRK);
                pdf.text(d.description || d.categorie, 16, y);
                pdf.setFont("helvetica", "normal");
                pdf.setTextColor(...MUT);
                pdf.text(`${d.categorie} · ${fmtDate(d.date)}`, 16, y + 4);
                pdf.setFont("helvetica", "bold");
                pdf.setTextColor(...GRN);
                pdf.text(fmt(d.montant), W - 16, y, { align: "right" });
                y += 12;
              });
              y += 4;
              pdf.setDrawColor(...BLU);
              pdf.setLineWidth(0.5);
              pdf.line(14, y, W - 14, y);
              y += 8;
              pdf.setFont("helvetica", "bold");
              pdf.setFontSize(11);
              pdf.setTextColor(...DRK);
              pdf.text(t.depensesTotalDuMois || "Total dépenses général du mois", 16, y);
              pdf.setTextColor(...GRN);
              pdf.text(fmt(totalDuMois), W - 16, y, { align: "right" });
              pdf.save(`depenses-general-${curKey}.pdf`);
            }}
            style={{
              width: "100%", padding: "10px 0",
              background: COL.header, color: "#F8F6F1",
              border: "none", fontSize: 12, fontWeight: 800,
              cursor: "pointer", fontFamily: "inherit",
            }}
          >
            📄 {t.telechargerPDF || "Télécharger en PDF"}
          </button>
        )}
      </div>
    </div>
  );
}

/* ── Onglet Stats ─────────────────────────────────────────── */
function TabStats({ depenses, voyage, t = {} }) {
  const list   = depenses || [];
  const now    = new Date();

  /* ── Kilométrage & Consommation globale ── */
  const allPleins = list.filter(d => d.type === "carburant");
  const sortedPleins = [...allPleins].sort((a, b) => Number(a.km) - Number(b.km));
  const kmCompteur = sortedPleins.length > 0 ? Number(sortedPleins[sortedPleins.length - 1].km) : 0;
  const kmTotal    = sortedPleins.length >= 2
    ? Number(sortedPleins[sortedPleins.length - 1].km) - Number(sortedPleins[0].km)
    : 0;
  let litresCalc = 0, kmCalc = 0;
  for (let i = 1; i < sortedPleins.length; i++) {
    const dKm = Number(sortedPleins[i].km) - Number(sortedPleins[i - 1].km);
    const L   = Number(sortedPleins[i].litres);
    if (dKm > 0 && L > 0) { kmCalc += dKm; litresCalc += L; }
  }
  const conso100   = kmCalc > 0 ? (litresCalc / kmCalc) * 100 : 0;
  const totalCarbu = allPleins.reduce((s, p) => s + Number(p.prix || 0), 0);
  const curKey = monthKey(now.toISOString());

  const carbuMois = allPleins
    .filter(p => monthKey(p.date) === curKey)
    .reduce((s, p) => s + Number(p.prix || 0), 0);
  const carbuAnnee = allPleins
    .filter(p => p.date && new Date(p.date).getFullYear() === now.getFullYear())
    .reduce((s, p) => s + Number(p.prix || 0), 0);

  const totalOf = arr => arr.reduce((s, d) => s + Number(d.montant || d.prix || 0), 0);

  const thisMon   = list.filter(d => monthKey(d.date) === curKey);
  const thisTotal = totalOf(thisMon);
  const thisKm    = computeKm(thisMon.filter(d => d.type === "carburant"));

  const ago12     = new Date(now.getFullYear() - 1, now.getMonth(), 1);
  const last12    = list.filter(d => new Date(d.date) >= ago12);
  const last12Tot = totalOf(last12);
  const last12Km  = computeKm(last12.filter(d => d.type === "carburant"));

  const genMois  = list.filter(d => monthKey(d.date) === curKey && d.type !== "carburant")
                       .reduce((s, d) => s + Number(d.montant || 0), 0);
  const gen12    = list.filter(d => new Date(d.date) >= ago12 && d.type !== "carburant")
                       .reduce((s, d) => s + Number(d.montant || 0), 0);

  /* ── Calcul stats voyage ── */
  const dep = voyage?.depart;
  const arr = voyage?.arrivee;
  const hasVoyage = !!(dep?.date && arr?.date);

  let vs = null;
  if (hasVoyage) {
    const depDate = new Date(dep.date);
    const arrDate = new Date(arr.date);
    const vList = list.filter(d => {
      if (!d.date) return false;
      const dDate = new Date(d.date);
      return dDate >= depDate && dDate <= arrDate;
    });
    const carbus   = vList.filter(d => d.type === "carburant");
    const catCost  = (c) => vList.filter(d => d.type === "general" && d.categorie === c)
                             .reduce((s, d) => s + Number(d.montant || 0), 0);
    vs = {
      depDate:      dep.date,
      arrDate:      arr.date,
      depKm:        dep.km ? Number(dep.km) : null,
      arrKm:        arr.km ? Number(arr.km) : null,
      duree:        Math.round((arrDate - depDate) / 86400000),
      kmParcourus:  dep.km && arr.km ? Number(arr.km) - Number(dep.km) : null,
      litres:       carbus.reduce((s, d) => s + Number(d.litres || 0), 0),
      totalCarbu:   carbus.reduce((s, d) => s + Number(d.prix   || 0), 0),
      totalEau:     catCost("Eau"),
      totalGaz:     catCost("Gaz"),
      totalCamping: catCost("Camping"),
      totalAire:    catCost("Aire de repos"),
      totalPeage:   catCost("Péage"),
      totalElec:    catCost("Électricité"),
      totalAll:     0,
    };
    vs.totalAll = vs.totalCarbu + vs.totalEau + vs.totalGaz + vs.totalCamping + vs.totalAire + vs.totalPeage + vs.totalElec;
  }

  /* ── PDF voyage ── */
  function handleVoyagePDF() {
    if (!vs) return;
    const pdf = new jsPDF({ unit: "mm", format: "a4" });
    const W = 210;
    const GRN  = [45, 90, 61];
    const BG   = [248, 246, 241];
    const DARK = [28, 28, 30];
    const MUT  = [138, 138, 142];
    const TER  = [196, 98, 45];
    const MINT = [234, 243, 222];
    const BORD = [211, 209, 199];

    const fmtLong = (s) => new Date(s).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
    const fmtKmN  = (n) => n != null ? String(Math.round(n)).replace(/\B(?=(\d{3})+(?!\d))/g, " ") : "—";

    // Header
    pdf.setFillColor(...GRN);
    pdf.rect(0, 0, W, 46, "F");
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(9);
    pdf.setTextColor(200, 230, 210);
    pdf.text("CHECKVAN", 20, 17);
    pdf.setFontSize(20);
    pdf.setTextColor(248, 246, 241);
    pdf.text("Rapport de voyage", 20, 29);
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(9);
    pdf.setTextColor(200, 230, 210);
    pdf.text(`Généré le ${new Date().toLocaleDateString("fr-FR")}`, 20, 40);

    // Période
    let y = 60;
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(7.5);
    pdf.setTextColor(...MUT);
    pdf.text("PÉRIODE DU VOYAGE", 20, y);
    y += 8;
    pdf.setFontSize(13);
    pdf.setTextColor(...DARK);
    pdf.text(`${fmtLong(vs.depDate)}  /  ${fmtLong(vs.arrDate)}`, 20, y);

    // Séparateur
    y += 10;
    pdf.setDrawColor(...BORD);
    pdf.setLineWidth(0.25);
    pdf.line(20, y, W - 20, y);

    // KPIs
    y += 12;
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(7.5);
    pdf.setTextColor(...MUT);
    pdf.text("CHIFFRES CLÉS", 20, y);
    y += 8;
    const KW = (W - 40) / 3;
    const drawKpi = (label, val, x) => {
      pdf.setFillColor(...BG);
      pdf.roundedRect(x, y - 5, KW - 4, 22, 2, 2, "F");
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(16);
      pdf.setTextColor(...GRN);
      pdf.text(val, x + (KW - 4) / 2, y + 7, { align: "center" });
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(7.5);
      pdf.setTextColor(...MUT);
      pdf.text(label, x + (KW - 4) / 2, y + 14, { align: "center" });
    };
    drawKpi("jours", String(vs.duree >= 0 ? vs.duree : "—"), 20);
    drawKpi("km parcourus", vs.kmParcourus != null ? fmtKmN(vs.kmParcourus) : "—", 20 + KW);

    // Troisième case : km départ / arrivée empilés
    const x3 = 20 + 2 * KW;
    pdf.setFillColor(...BG);
    pdf.roundedRect(x3, y - 5, KW - 4, 22, 2, 2, "F");
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(11);
    pdf.setTextColor(...GRN);
    pdf.text(vs.depKm ? fmtKmN(vs.depKm) + " km" : "—", x3 + (KW - 4) / 2, y + 3, { align: "center" });
    pdf.setTextColor(...DARK);
    pdf.text(vs.arrKm ? fmtKmN(vs.arrKm) + " km" : "—", x3 + (KW - 4) / 2, y + 10, { align: "center" });
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(7.5);
    pdf.setTextColor(...MUT);
    pdf.text("depart / arrivee", x3 + (KW - 4) / 2, y + 16, { align: "center" });

    // Séparateur
    y += 30;
    pdf.setDrawColor(...BORD);
    pdf.setLineWidth(0.25);
    pdf.line(20, y, W - 20, y);

    // Tableau dépenses
    y += 12;
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(7.5);
    pdf.setTextColor(...MUT);
    pdf.text("DÉPENSES DU VOYAGE", 20, y);
    y += 8;
    pdf.setFontSize(18);
    pdf.setTextColor(...DARK);
    pdf.text(fmt(vs.totalAll), 20, y);
    y += 12;

    const rows = [
      { label: "Essence",      value: vs.litres > 0 ? `${vs.litres.toFixed(1)} L — ${fmt(vs.totalCarbu)}` : fmt(vs.totalCarbu), amount: vs.totalCarbu, rgb: TER },
      { label: "Eau",          value: fmt(vs.totalEau),     amount: vs.totalEau,     rgb: [91, 141, 184] },
      { label: "Gaz",          value: fmt(vs.totalGaz),     amount: vs.totalGaz,     rgb: [239, 159, 39] },
      { label: "Électricité",  value: fmt(vs.totalElec),    amount: vs.totalElec,    rgb: [91, 141, 184] },
      { label: "Camping",      value: fmt(vs.totalCamping), amount: vs.totalCamping, rgb: [124, 110, 168] },
      { label: "Aire de repos",value: fmt(vs.totalAire),    amount: vs.totalAire,    rgb: [91, 141, 184] },
      { label: "Péage",        value: fmt(vs.totalPeage),   amount: vs.totalPeage,   rgb: [91, 141, 184] },
    ].filter(r => r.amount > 0);

    pdf.setFillColor(...GRN);
    pdf.rect(20, y - 4.5, W - 40, 8, "F");
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(7.5);
    pdf.setTextColor(248, 246, 241);
    pdf.text("CATÉGORIE", 27, y);
    pdf.text("MONTANT / QUANTITÉ", W - 24, y, { align: "right" });
    pdf.text("PART", W - 62, y, { align: "right" });
    y += 5;

    rows.forEach((row, i) => {
      pdf.setFillColor(...(i % 2 === 0 ? BG : [255, 255, 255]));
      pdf.rect(20, y - 3.5, W - 40, 9, "F");
      pdf.setFillColor(...row.rgb);
      pdf.rect(20, y - 3.5, 3, 9, "F");
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(9.5);
      pdf.setTextColor(...DARK);
      pdf.text(row.label, 27, y + 1.5);
      pdf.setFont("helvetica", "bold");
      pdf.setTextColor(...row.rgb);
      pdf.text(row.value, W - 24, y + 1.5, { align: "right" });
      const pct = vs.totalAll > 0 ? Math.round((row.amount / vs.totalAll) * 100) : 0;
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(8.5);
      pdf.setTextColor(...MUT);
      pdf.text(`${pct} %`, W - 62, y + 1.5, { align: "right" });
      y += 9;
    });

    // Cachet
    y = Math.max(y + 16, 242);
    pdf.setFillColor(...MINT);
    pdf.setDrawColor(93, 202, 165);
    pdf.setLineWidth(0.7);
    pdf.roundedRect(20, y, W - 40, 26, 3, 3, "FD");
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(10);
    pdf.setTextColor(8, 80, 65);
    pdf.text("Genere par CheckVan", W / 2, y + 11, { align: "center" });
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(8);
    pdf.setTextColor(...MUT);
    pdf.text(`checkvan.checkapp-studio.fr  -  ${new Date().toLocaleString("fr-FR")}`, W / 2, y + 19, { align: "center" });

    pdf.save(`checkvan-voyage-${(vs.depDate || "").slice(0, 7)}.pdf`);
  }

  const StatBox = ({ label, value, sub }) => (
    <div>
      <div style={{ fontSize: 14, fontWeight: 900, color: COL.header }}>{value}</div>
      <div style={{ fontSize: 9, color: COL.muted, marginTop: 2 }}>{sub}</div>
    </div>
  );

  const StatRow = ({ label, value, sub, color = COL.text, bold }) => (
    <div style={{
      display: "flex", justifyContent: "space-between", alignItems: "center",
      padding: "7px 0", borderBottom: `1px solid ${COL.border}`,
    }}>
      <span style={{ fontSize: 11, color: COL.muted }}>{label}</span>
      <div style={{ textAlign: "right" }}>
        <div style={{ fontSize: 11, fontWeight: bold ? 900 : 700, color }}>{value}</div>
        {sub && <div style={{ fontSize: 9, color: COL.muted, marginTop: 1 }}>{sub}</div>}
      </div>
    </div>
  );

  return (
    <div>
      {/* Conso+Carbu & Total général — deux carrés côte à côte */}
      <div style={{ display: "flex", gap: 8, marginBottom: 6 }}>
        {/* Carré Consommation + Carburant */}
        <div style={{
          flex: 1, background: "#FFFDE7", borderRadius: 10,
          padding: "10px", boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
          border: "1px solid #FFF176",
        }}>
          <div style={{ fontSize: 9, fontWeight: 700, color: COL.muted, letterSpacing: 1, marginBottom: 5 }}>
            {t.consommation || "CONSOMMATION"}
          </div>
          <div style={{ fontSize: 13, fontWeight: 900, color: COL.header, lineHeight: 1 }}>
            {conso100 > 0 ? conso100.toFixed(1).replace(".", ",") : "—"}
          </div>
          <div style={{ fontSize: 9, color: COL.muted, marginTop: 2 }}>L / 100 km</div>
          <div style={{ height: 1, background: "#FFF176", margin: "6px 0" }} />
          <div style={{ fontSize: 9, fontWeight: 700, color: COL.muted, letterSpacing: 1, marginBottom: 5 }}>
            {t.carburant ? t.carburant.toUpperCase() : "CARBURANT"}
          </div>
          <div style={{ fontSize: 13, fontWeight: 900, color: COL.header, lineHeight: 1 }}>
            {fmt(carbuMois)}
          </div>
          <div style={{ fontSize: 9, color: COL.muted, marginTop: 2, textTransform: "capitalize" }}>
            {monthLabel(curKey)}
          </div>
          <div style={{ height: 1, background: "#FFF176", margin: "6px 0" }} />
          <div style={{ fontSize: 13, fontWeight: 900, color: COL.header, lineHeight: 1 }}>
            {fmt(carbuAnnee)}
          </div>
          <div style={{ fontSize: 9, color: COL.muted, marginTop: 2 }}>{now.getFullYear()}</div>
        </div>

        {/* Carré Total dépenses général */}
        <div style={{
          flex: 1, background: "#E3F2FD", borderRadius: 10,
          padding: "10px", boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
          border: "1px solid #90CAF9",
        }}>
          <div style={{ fontSize: 9, fontWeight: 700, color: COL.muted, letterSpacing: 1, marginBottom: 5 }}>
            {t.totalDepensesGeneral || "TOTAL GÉNÉRAL"}
          </div>
          <div style={{ fontSize: 13, fontWeight: 900, color: COL.header, lineHeight: 1 }}>
            {fmt(genMois)}
          </div>
          <div style={{ fontSize: 9, color: COL.muted, marginTop: 2, textTransform: "capitalize" }}>
            {monthLabel(curKey)}
          </div>
          <div style={{ height: 1, background: "#90CAF9", margin: "6px 0" }} />
          <div style={{ fontSize: 9, fontWeight: 700, color: COL.muted, letterSpacing: 1, marginBottom: 5 }}>
            {t.douzeMois || "12 DERNIERS MOIS"}
          </div>
          <div style={{ fontSize: 13, fontWeight: 900, color: COL.header, lineHeight: 1 }}>
            {fmt(gen12)}
          </div>
          <div style={{ fontSize: 9, color: COL.muted, marginTop: 2 }}>
            {now.getFullYear() - 1} – {now.getFullYear()}
          </div>
        </div>
      </div>

      {/* Dépenses total général + carburant */}
      <div style={{ fontSize: 9, fontWeight: 700, color: "white", letterSpacing: 1, marginBottom: 4 }}>
        {t.depensesTotalGeneralCarbu || "DÉPENSES TOTAL GÉNÉRAL + CARBURANT"}
      </div>
      <div style={{
        background: "white", borderRadius: 10, padding: "10px",
        marginBottom: 6, boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
      }}>
        {/* Ce mois-ci */}
        <div style={{ fontSize: 9, fontWeight: 700, color: COL.muted, letterSpacing: 1, marginBottom: 5 }}>
          {t.ceMoisCi || "CE MOIS-CI"}
        </div>
        <div style={{ display: "flex" }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 900, color: COL.header, lineHeight: 1 }}>{fmt(thisTotal)}</div>
            <div style={{ fontSize: 9, color: COL.muted, marginTop: 2 }}>{t.depensesTotalesLabel || "dépenses totales"}</div>
          </div>
          <div style={{ width: 1, background: COL.border, margin: "0 10px" }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 900, color: COL.header, lineHeight: 1 }}>
              {thisKm > 0 ? thisKm.toLocaleString("fr-FR") : "—"}
            </div>
            <div style={{ fontSize: 9, color: COL.muted, marginTop: 2 }}>{t.parcourus || "parcourus"}</div>
          </div>
        </div>
        <div style={{ height: 1, background: COL.border, margin: "8px 0" }} />
        {/* 12 derniers mois */}
        <div style={{ fontSize: 9, fontWeight: 700, color: COL.muted, letterSpacing: 1, marginBottom: 5 }}>
          {t.douzeMois || "12 DERNIERS MOIS"}
        </div>
        <div style={{ display: "flex" }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 900, color: COL.header, lineHeight: 1 }}>{fmt(last12Tot)}</div>
            <div style={{ fontSize: 9, color: COL.muted, marginTop: 2 }}>{t.depensesTotalesLabel || "dépenses totales"}</div>
          </div>
          <div style={{ width: 1, background: COL.border, margin: "0 10px" }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 900, color: COL.header, lineHeight: 1 }}>
              {last12Km > 0 ? last12Km.toLocaleString("fr-FR") : "—"}
            </div>
            <div style={{ fontSize: 9, color: COL.muted, marginTop: 2 }}>{t.parcourus || "parcourus"}</div>
          </div>
        </div>
      </div>

      {/* ── Voyage ── */}
      <div style={{ marginTop: 8 }}>
        <div style={{ fontSize: 9, fontWeight: 700, color: "white", letterSpacing: 1, marginBottom: 6 }}>
          {t.voyageSection || "VOYAGE"}
        </div>

        {!hasVoyage ? (
          <div style={{
            backgroundColor: "#d4edda", borderRadius: 12, padding: "14px 12px",
            boxShadow: "0 1px 6px rgba(0,0,0,0.06)",
            border: "2px solid #2D5A3D",
            textAlign: "center", color: COL.muted, fontSize: 11,
          }}>
            {t.aucunVoyageDepenses || "Aucun voyage enregistré — renseignez les dates de départ et d'arrivée dans l'onglet Dépenses."}
          </div>
        ) : (
          <div style={{
            backgroundColor: "#d4edda", borderRadius: "12px", padding: "10px",
            boxShadow: "0 1px 6px rgba(0,0,0,0.06)",
            border: "2px solid #2D5A3D",
          }}>
            {/* Départ / Arrivée */}
            <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
              {[
                { lbl: "DÉPART",  date: vs.depDate, km: vs.depKm },
                { lbl: "ARRIVÉE", date: vs.arrDate, km: vs.arrKm },
              ].map(({ lbl, date, km }) => (
                <div key={lbl} style={{
                  flex: 1, background: COL.bg, borderRadius: 8,
                  padding: "7px 9px", border: "1px solid rgba(255,255,255,0.25)",
                }}>
                  <div style={{ fontSize: 8, fontWeight: 800, color: "white", letterSpacing: 1, marginBottom: 3 }}>{lbl}</div>
                  <div style={{ fontSize: 11, fontWeight: 800, color: "white" }}>
                    {new Date(date).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" })}
                  </div>
                  {km != null && (
                    <div style={{ fontSize: 9, color: "rgba(255,255,255,0.75)", marginTop: 2 }}>
                      {km.toLocaleString("fr-FR")} km
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* KPIs durée + km */}
            <div style={{ display: "flex", marginBottom: 10 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 20, fontWeight: 900, color: COL.header, lineHeight: 1 }}>
                  {vs.duree >= 0 ? vs.duree : "—"}
                </div>
                <div style={{ fontSize: 9, color: COL.muted, marginTop: 2 }}>{t.jours || "jours"}</div>
              </div>
              <div style={{ width: 1, background: COL.border, margin: "0 12px" }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 20, fontWeight: 900, color: COL.text, lineHeight: 1 }}>
                  {vs.kmParcourus != null ? vs.kmParcourus.toLocaleString("fr-FR") : "—"}
                </div>
                <div style={{ fontSize: 9, color: COL.muted, marginTop: 2 }}>{t.kmParcourus || "km parcourus"}</div>
              </div>
            </div>

            {/* Lignes dépenses */}
            <div style={{ borderTop: `1px solid ${COL.border}` }}>
              {[
                { label: t.essence    || "Essence",       amount: vs.totalCarbu,   sub: vs.litres > 0 ? `${vs.litres.toFixed(1)} L` : null, color: "#C4622D" },
                { label: t.eau        || "Eau",           amount: vs.totalEau,     color: "#5B8DB8" },
                { label: t.gaz        || "Gaz",           amount: vs.totalGaz,     color: "#EF9F27" },
                { label: t.electricite|| "Électricité",   amount: vs.totalElec,    color: "#5B8DB8" },
                { label: t.camping    || "Camping",       amount: vs.totalCamping, color: "#7C6EA8" },
                { label: t.aireDeRepos|| "Aire de repos", amount: vs.totalAire,    color: "#5B8DB8" },
                { label: t.peage      || "Péage",         amount: vs.totalPeage,   color: "#5B8DB8" },
              ]
                .filter(r => r.amount > 0)
                .map(r => {
                  const pct = vs.totalAll > 0 ? Math.round((r.amount / vs.totalAll) * 100) : 0;
                  return (
                    <div key={r.label} style={{ padding: "7px 0", borderBottom: `1px solid ${COL.border}` }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                        <div>
                          <span style={{ fontSize: 11, fontWeight: 700, color: COL.text }}>{r.label}</span>
                          {r.sub && <span style={{ fontSize: 9, color: COL.muted, marginLeft: 5 }}>{r.sub}</span>}
                        </div>
                        <div style={{ textAlign: "right" }}>
                          <span style={{ fontSize: 11, fontWeight: 700, color: r.color }}>{fmt(r.amount)}</span>
                          {pct > 0 && <span style={{ fontSize: 9, color: COL.muted, marginLeft: 4 }}>{pct}%</span>}
                        </div>
                      </div>
                      <div style={{ height: 4, background: "#E8E6DF", borderRadius: 99, overflow: "hidden" }}>
                        <div style={{ width: `${pct}%`, height: "100%", background: r.color, borderRadius: 99, transition: "width 0.4s ease" }} />
                      </div>
                    </div>
                  );
                })
              }
              {/* Total */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingTop: 8, marginTop: 2 }}>
                <span style={{ fontSize: 12, fontWeight: 800, color: COL.text }}>{t.totalVoyage || "Total voyage"}</span>
                <span style={{ fontSize: 15, fontWeight: 900, color: COL.header }}>{fmt(vs.totalAll)}</span>
              </div>
            </div>

            {/* Bouton PDF */}
            <button
              onClick={handleVoyagePDF}
              style={{
                width: "100%", padding: "9px 0", marginTop: 10,
                background: COL.header, color: "#F8F6F1",
                border: "none", borderRadius: 10,
                fontSize: 12, fontWeight: 800,
                cursor: "pointer", fontFamily: "inherit",
                boxShadow: "0 2px 8px rgba(45,90,61,0.2)",
              }}
            >
              {t.telechargerPDF || "Télécharger en PDF"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Cartes Départ / Arrivée ──────────────────────────────── */
function VoyageDateCard({ label, data, onSave, kmActuel, isDepart = false, t = {} }) {
  const hasData = !!data?.date;
  const [editing,   setEditing]   = useState(!hasData);
  const [editDate,  setEditDate]  = useState(data?.date || "");
  const [editKm,    setEditKm]    = useState(data?.km || (isDepart && kmActuel ? String(kmActuel) : ""));
  const [flash,     setFlash]     = useState(false);

  useEffect(() => {
    setEditDate(data?.date || "");
    setEditKm(data?.km || (isDepart && kmActuel ? String(kmActuel) : ""));
    if (data?.date) setEditing(false);
  }, [data?.date, data?.km, kmActuel, isDepart]);

  const handleSave = () => {
    if (!editDate) return;
    onSave({ date: editDate, km: editKm });
    setFlash(true);
    setEditing(false);
    setTimeout(() => setFlash(false), 2000);
  };

  const inputStyle = {
    width: "100%", padding: "9px 10px",
    borderRadius: 9, border: `1.5px solid ${COL.border}`,
    background: "#F0FAF4", color: COL.text,
    fontSize: 13, outline: "none",
    fontFamily: "inherit", boxSizing: "border-box",
  };

  return (
    <div style={{
      flex: 1, background: "#d4edda", borderRadius: 14,
      padding: "14px 12px",
      border: "2px solid #2D5A3D",
      boxShadow: "0 1px 8px rgba(0,0,0,0.06)",
      display: "flex", flexDirection: "column", gap: 8,
    }}>
      <div style={{ fontSize: 11, fontWeight: 800, color: COL.header, letterSpacing: 1 }}>
        {label.toUpperCase()}
      </div>

      {editing ? (
        <>
          <input type="date" value={editDate} onChange={e => setEditDate(e.target.value)} style={inputStyle} />
          <div style={{ fontSize: 10, fontWeight: 700, color: "#2D5A3D", letterSpacing: 0.8, marginBottom: 4 }}>{t.kmCompteurLabel || "KM COMPTEUR"}</div>
          <input
            type="number" value={editKm} onChange={e => setEditKm(e.target.value)}
            placeholder="Ex : 45 230"
            style={{ ...inputStyle }}
          />
          <button onClick={handleSave} style={{
            width: "100%", padding: "10px 0",
            borderRadius: 9, border: "none",
            background: COL.header, color: "#F8F6F1",
            fontSize: 12, fontWeight: 800,
            cursor: "pointer", fontFamily: "inherit",
          }}>
            {flash ? (t.enregistreFlash || "✓ Enregistré") : (t.enregistrerBtn || "Enregistrer")}
          </button>
        </>
      ) : (
        <>
          <div style={{ fontSize: 15, fontWeight: 900, color: COL.text, lineHeight: 1.2 }}>
            {new Date(data.date).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" })}
          </div>
          {data.km && (
            <div style={{ fontSize: 12, color: COL.muted, fontWeight: 600 }}>
              {Number(data.km).toLocaleString("fr-FR")} km
            </div>
          )}
          <button onClick={() => setEditing(true)} style={{
            width: "100%", padding: "8px 0",
            borderRadius: 9, border: `1.5px solid ${COL.border}`,
            background: "transparent", color: COL.muted,
            fontSize: 11, fontWeight: 700,
            cursor: "pointer", fontFamily: "inherit",
          }}>
            {t.modifier || "Modifier"}
          </button>
        </>
      )}
    </div>
  );
}

/* ── Écran principal ──────────────────────────────────────── */
const TABS = [
  { id: "carburant", label: "Carburant" },
  { id: "general",   label: "Général"   },
  { id: "stats",     label: "Stats"     },
];

export default function DepensesScreen({ depenses, setDepenses, voyage, onVoyageChange, userId, t = {} }) {
  const [tab, setTab] = useState("carburant");

  const kmActuel = Math.max(...(depenses || []).filter(d => d.type === "carburant" && d.km).map(d => Number(d.km)).filter(k => k > 0), 0) || "";

  const saveDepart  = (val) => onVoyageChange?.({ ...(voyage || {}), depart:  val });
  const saveArrivee = (val) => onVoyageChange?.({ ...(voyage || {}), arrivee: val });

  return (
    <div style={{ background: COL.bg, minHeight: "calc(100vh - 118px)", paddingBottom: 24 }}>

      {/* Header */}
      <div style={{ background: COL.header, padding: "20px 20px 18px" }}>
        <div style={{ fontSize: 22, fontWeight: 900, color: "#F8F6F1", textAlign: "center" }}>
          {t.depenses || "Dépenses"}
        </div>
      </div>

      {/* Cartes Départ / Arrivée */}
      <div style={{ padding: "14px 16px 0", display: "flex", gap: 10 }}>
        <VoyageDateCard label={t.departLabel || "Départ"}  data={voyage?.depart}  onSave={saveDepart}  kmActuel={kmActuel} isDepart t={t} />
        <VoyageDateCard label={t.arriveeLabel || "Arrivée"} data={voyage?.arrivee} onSave={saveArrivee} t={t} />
      </div>

      <div style={{ padding: "16px 16px 0" }}>

        {/* Onglets */}
        <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
          {TABS.map(tb => {
            const active = tb.id === tab;
            return (
              <button
                key={tb.id}
                onClick={() => setTab(tb.id)}
                style={{
                  flex: 1, padding: "10px 0", borderRadius: 10,
                  border: `1.5px solid ${active ? COL.header : "rgba(255,255,255,0.35)"}`,
                  background: active ? COL.header : "rgba(255,255,255,0.15)",
                  color: "white",
                  fontSize: 13, fontWeight: 700,
                  cursor: "pointer", fontFamily: "inherit",
                  transition: "all 0.15s",
                }}
              >
                {({ carburant: t.carburant, general: t.general, stats: t.stats })[tb.id] || tb.label}
              </button>
            );
          })}
        </div>

        {tab === "carburant" && (
          <TabCarburant depenses={depenses} setDepenses={setDepenses} t={t} />
        )}
        {tab === "general" && (
          <TabGeneral depenses={depenses} setDepenses={setDepenses} userId={userId} t={t} />
        )}
        {tab === "stats" && (
          <TabStats depenses={depenses} voyage={voyage} t={t} />
        )}
      </div>
    </div>
  );
}
